import { useEffect, useRef, useState } from "react";
import { GlobalWorkerOptions, getDocument, type PDFDocumentProxy } from "pdfjs-dist";
import type { EditorField } from "@/lib/sign/templateEditorModel";
import { resolvePreviewValue } from "@/lib/sign/templateEditorModel";
import { SignTemplateFieldOverlay } from "./SignTemplateFieldOverlay";

GlobalWorkerOptions.workerSrc = new URL(
  "pdfjs-dist/build/pdf.worker.min.mjs",
  import.meta.url
).toString();

type Props = {
  pdfUrl: string;
  page: number;
  scale: number;
  fields: EditorField[];
  selectedFieldId: string | null;
  preview: boolean;
  sampleFixture: Record<string, unknown>;
  readOnly?: boolean;
  onPageCount?: (count: number) => void;
  onSelectField: (id: string | null) => void;
  onMoveField: (id: string, patch: Partial<Pick<EditorField, "x" | "y">>) => void;
};

export function SignTemplatePdfPage({
  pdfUrl,
  page,
  scale,
  fields,
  selectedFieldId,
  preview,
  sampleFixture,
  readOnly,
  onPageCount,
  onSelectField,
  onMoveField,
}: Props) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [doc, setDoc] = useState<PDFDocumentProxy | null>(null);
  const [pageSize, setPageSize] = useState({ width: 612, height: 792 });
  const [loadError, setLoadError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoadError(null);
    void (async () => {
      try {
        const loaded = await getDocument(pdfUrl).promise;
        if (!cancelled) {
          setDoc(loaded);
          onPageCount?.(loaded.numPages);
        }
      } catch (err) {
        if (!cancelled) {
          setLoadError(err instanceof Error ? err.message : "Failed to load PDF");
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [pdfUrl]);

  useEffect(() => {
    if (!doc || !canvasRef.current) return undefined;
    let cancelled = false;

    void (async () => {
      const pdfPage = await doc.getPage(page);
      const viewport = pdfPage.getViewport({ scale: 1 });
      if (!cancelled) setPageSize({ width: viewport.width, height: viewport.height });

      const renderViewport = pdfPage.getViewport({ scale });
      const canvas = canvasRef.current!;
      const ctx = canvas.getContext("2d");
      if (!ctx) return;

      canvas.width = renderViewport.width;
      canvas.height = renderViewport.height;
      await pdfPage.render({ canvasContext: ctx, viewport: renderViewport, canvas }).promise;
    })();

    return () => {
      cancelled = true;
    };
  }, [doc, page, scale]);

  const pageFields = fields.filter((f) => f.page === page);

  return (
    <div className="relative inline-block max-w-full overflow-auto rounded-lg border border-gray-200 bg-gray-100 dark:border-zinc-700 dark:bg-zinc-800">
      {loadError ? (
        <div className="p-6 text-sm text-red-700 dark:text-red-200">{loadError}</div>
      ) : (
        <>
          <canvas ref={canvasRef} className="block max-w-full" />
          <div
            className="absolute left-0 top-0"
            style={{
              width: pageSize.width * scale,
              height: pageSize.height * scale,
              pointerEvents: readOnly ? "none" : "auto",
            }}
            onClick={() => onSelectField(null)}
          >
            {pageFields.map((field) => (
              <SignTemplateFieldOverlay
                key={field.id}
                field={field}
                scale={scale}
                selected={field.id === selectedFieldId}
                previewText={
                  preview && field.kind === "text"
                    ? resolvePreviewValue(field.dataPath, sampleFixture)
                    : undefined
                }
                onSelect={() => onSelectField(field.id)}
                onMove={(patch) => onMoveField(field.id, patch)}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
}
